// src/components/TrialManagementDashboard.js
import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getDatabase, 
  ref, 
  get, 
  query, 
  orderByChild, 
  push, 
  remove, 
  update,
  equalTo 
} from 'firebase/database';
import Spinner from './Spinner'; // Import the new Spinner component
import './TrialManagementDashboard.css';

// Firebase configuration - use your existing configuration
const firebaseConfig = {
  apiKey: "AIzaSyAF_vfXQtqMyPITZc7vGRtT2-IdIPbZ8qE",
  authDomain: "blue-comet-free-trial.firebaseapp.com",
  databaseURL: "https://blue-comet-free-trial-default-rtdb.firebaseio.com",
  projectId: "blue-comet-free-trial",
  storageBucket: "blue-comet-free-trial.firebasestorage.app",
  messagingSenderId: "421726724578",
  appId: "1:421726724578:web:ef2a2ed2378053d4fe59bb",
  measurementId: "G-TS4M3HFV2Z"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

function TrialManagementDashboard() {
  const [trialUsage, setTrialUsage] = useState([]);
  const [blacklistedUsers, setBlacklistedUsers] = useState([]);
  const [fingerprint, setFingerprint] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [viewMode, setViewMode] = useState('usage'); // 'usage' or 'blacklist'
  const [usageSummary, setUsageSummary] = useState({
    totalPrompts: 0,
    activeUsers: 0,
    completedTrials: 0
  });
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isPurging, setIsPurging] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load trial usage data
      const usageRef = ref(database, 'trialUsage');
      const usageSnapshot = await get(usageRef);
      
      // Load used trials data to get all fingerprints
      const usedTrialsRef = ref(database, 'usedTrials');
      const usedTrialsSnapshot = await get(usedTrialsRef);
      
      // Load blacklist data
      const blacklistRef = ref(database, 'blacklist');
      const blacklistSnapshot = await get(blacklistRef);
      
      if (usageSnapshot.exists() || usedTrialsSnapshot.exists()) {
        // Process usage data
        const usageData = [];
        const usersMap = new Map(); // Map to track unique users
        const activeUsers = new Set(); // Set to track active users
        const completedTrials = new Set(); // Set to track completed trials
        let totalPrompts = 0;
        
        // First collect all unique fingerprints from usedTrials
        if (usedTrialsSnapshot.exists()) {
          const trials = {};
          usedTrialsSnapshot.forEach((childSnapshot) => {
            const key = childSnapshot.key;
            const data = childSnapshot.val();
            trials[key] = { ...data, id: key };
          });
          
          Object.values(trials).forEach(trial => {
            if (trial.fingerprint) {
              usersMap.set(trial.fingerprint, {
                fingerprint: trial.fingerprint,
                startedAt: trial.timestamp,
                trialKeyId: trial.trialKeyId,
                deviceData: trial.deviceData,
                promptCount: 0,
                isActive: true,
                usedTrialId: trial.id // Store record ID for deletion
              });
            }
          });
        }
        
        // Then process usage data
        if (usageSnapshot.exists()) {
          const usage = {};
          
          // Convert snapshot to array with record IDs
          usageSnapshot.forEach((childSnapshot) => {
            const key = childSnapshot.key;
            const data = childSnapshot.val();
            usage[key] = { ...data, id: key };
          });
          
          // Store all usage records by fingerprint for potential deletion
          const usageRecordsByFingerprint = {};
          
          // Count total prompts and update usage per fingerprint
          Object.values(usage).forEach(record => {
            totalPrompts++;
            
            if (record.fingerprint) {
              // Store record ID indexed by fingerprint for deletion purposes
              if (!usageRecordsByFingerprint[record.fingerprint]) {
                usageRecordsByFingerprint[record.fingerprint] = [];
              }
              usageRecordsByFingerprint[record.fingerprint].push(record.id);
              
              activeUsers.add(record.fingerprint);
              
              if (usersMap.has(record.fingerprint)) {
                const userData = usersMap.get(record.fingerprint);
                userData.promptCount = (userData.promptCount || 0) + 1;
                userData.lastUsed = record.timestamp;
                userData.remaining = record.remaining;
                
                // Store usage record IDs for potential deletion
                userData.usageRecordIds = usageRecordsByFingerprint[record.fingerprint];
                
                // If remaining is 0, mark as completed
                if (record.remaining === 0) {
                  completedTrials.add(record.fingerprint);
                  userData.isActive = false;
                }
                
                usersMap.set(record.fingerprint, userData);
              }
            }
          });
        }
        
        // Convert map to array and sort by last used time
        const usageArray = Array.from(usersMap.values())
          .sort((a, b) => {
            return new Date(b.lastUsed || b.startedAt) - new Date(a.lastUsed || a.startedAt);
          });
        
        setTrialUsage(usageArray);
        setUsageSummary({
          totalPrompts,
          activeUsers: activeUsers.size,
          completedTrials: completedTrials.size
        });
      } else {
        setTrialUsage([]);
        setUsageSummary({
          totalPrompts: 0,
          activeUsers: 0,
          completedTrials: 0
        });
      }
      
      // Process blacklist data
      if (blacklistSnapshot.exists()) {
        const blacklist = [];
        blacklistSnapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          const data = childSnapshot.val();
          blacklist.push({ id: key, ...data });
        });
        setBlacklistedUsers(blacklist);
      } else {
        setBlacklistedUsers([]);
      }
      
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add a user to the blacklist
  const addToBlacklist = async () => {
    if (!fingerprint.trim()) {
      setError("Please enter a valid fingerprint ID");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if fingerprint already exists in blacklist
      const blacklistRef = ref(database, 'blacklist');
      const blacklistQuery = query(blacklistRef, orderByChild('fingerprint'), equalTo(fingerprint));
      const snapshot = await get(blacklistQuery);
      
      if (snapshot.exists()) {
        setError("This fingerprint is already blacklisted");
        setIsLoading(false);
        return;
      }
      
      // Add to blacklist
      await push(blacklistRef, {
        fingerprint: fingerprint,
        timestamp: new Date().toISOString(),
        reason: "Added via admin dashboard"
      });
      
      setActionSuccess("User successfully added to blacklist");
      setFingerprint('');
      
      // Reload data to refresh the UI
      await loadData();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error("Error adding to blacklist:", err);
      setError("Failed to add to blacklist: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Remove a user from the blacklist
  const removeFromBlacklist = async (id) => {
    if (!id) return;
    
    if (!window.confirm("Are you sure you want to remove this user from the blacklist?")) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove from blacklist
      const blacklistItemRef = ref(database, `blacklist/${id}`);
      await remove(blacklistItemRef);
      
      setActionSuccess("User successfully removed from blacklist");
      
      // Reload data to refresh the UI
      await loadData();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error("Error removing from blacklist:", err);
      setError("Failed to remove from blacklist: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete all user data from Firebase
  const deleteUserData = async (fingerprint, userData) => {
    if (!fingerprint) return;
    
    if (!window.confirm("Are you sure you want to completely delete this user's data? This will allow them to get a new trial on next login.")) {
      return;
    }
    
    setIsDeletingUser(true);
    setError(null);
    
    try {
      let deletionCount = 0;
      
      // 1. Delete from usedTrials first
      if (userData.usedTrialId) {
        console.log("Deleting user from usedTrials:", userData.usedTrialId);
        const usedTrialsRef = ref(database, `usedTrials/${userData.usedTrialId}`);
        await remove(usedTrialsRef);
        deletionCount++;
      }
      
      // 2. Delete all usage records for this fingerprint
      if (userData.usageRecordIds && userData.usageRecordIds.length > 0) {
        console.log(`Deleting ${userData.usageRecordIds.length} usage records`);
        for (const recordId of userData.usageRecordIds) {
          const usageRef = ref(database, `trialUsage/${recordId}`);
          await remove(usageRef);
          deletionCount++;
        }
      }
      
      // 3. Check if user is in blacklist and remove if present
      const blacklistRef = ref(database, 'blacklist');
      const blacklistQuery = query(blacklistRef, orderByChild('fingerprint'), equalTo(fingerprint));
      const blacklistSnapshot = await get(blacklistQuery);
      
      if (blacklistSnapshot.exists()) {
        console.log("Found user in blacklist, removing");
        blacklistSnapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          remove(ref(database, `blacklist/${key}`));
          deletionCount++;
        });
      }
      
      // 4. Also search for and delete from trialInitializations and trialExpirations
      const initializationsRef = ref(database, 'trialInitializations');
      const initQuery = query(initializationsRef, orderByChild('fingerprint'), equalTo(fingerprint));
      const initSnapshot = await get(initQuery);
      
      if (initSnapshot.exists()) {
        console.log("Deleting from trialInitializations");
        initSnapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          remove(ref(database, `trialInitializations/${key}`));
          deletionCount++;
        });
      }
      
      const expirationsRef = ref(database, 'trialExpirations');
      const expQuery = query(expirationsRef, orderByChild('fingerprint'), equalTo(fingerprint));
      const expSnapshot = await get(expQuery);
      
      if (expSnapshot.exists()) {
        console.log("Deleting from trialExpirations");
        expSnapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          remove(ref(database, `trialExpirations/${key}`));
          deletionCount++;
        });
      }
      
      setActionSuccess(`Successfully deleted ${deletionCount} record(s) for user ${fingerprint.substring(0, 8)}...`);
      
      // Reload data to refresh the UI
      await loadData();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 5000);
      
    } catch (err) {
      console.error("Error deleting user data:", err);
      setError("Failed to delete user data: " + err.message);
    } finally {
      setIsDeletingUser(false);
    }
  };

  // NEW: Purge all trial data from Firebase
  const purgeAllTrialData = async () => {
    // Triple confirmation to prevent accidental data loss
    if (!window.confirm("⚠️ WARNING: This will delete ALL trial data from the database. This action cannot be undone.")) {
      return;
    }
    
    if (!window.confirm("Are you ABSOLUTELY SURE you want to purge all trial data? All users will be able to start new trials.")) {
      return;
    }
    
    const confirmInput = window.prompt("Type 'PURGE ALL DATA' to confirm this irreversible action:");
    if (confirmInput !== "PURGE ALL DATA") {
      setError("Purge operation cancelled - confirmation text didn't match.");
      return;
    }
    
    setIsPurging(true);
    setError(null);
    
    try {
      let deletionCounter = 0;
      const tables = [
        'trialUsage',
        'usedTrials',
        'blacklist',
        'trialInitializations',
        'trialExpirations',
        'trialKeys'
      ];
      
      // Record deletion start time
      const startTime = Date.now();
      
      // Process each table
      for (const tableName of tables) {
        setActionSuccess(`Purging ${tableName}...`);
        
        // Get all records from the table
        const tableRef = ref(database, tableName);
        const snapshot = await get(tableRef);
        
        if (snapshot.exists()) {
          // Create a record of how many we're deleting
          let tableCount = 0;
          snapshot.forEach(() => tableCount++);
          console.log(`Found ${tableCount} records in ${tableName}`);
          
          // Delete each record
          const promises = [];
          snapshot.forEach((childSnapshot) => {
            const key = childSnapshot.key;
            const recordRef = ref(database, `${tableName}/${key}`);
            promises.push(remove(recordRef));
            deletionCounter++;
          });
          
          // Wait for all deletions to complete
          await Promise.all(promises);
          console.log(`Deleted ${tableCount} records from ${tableName}`);
        } else {
          console.log(`No records found in ${tableName}`);
        }
      }
      
      // Calculate the time taken
      const endTime = Date.now();
      const timeInSeconds = ((endTime - startTime) / 1000).toFixed(2);
      
      // Show success message
      setActionSuccess(`Successfully purged ${deletionCounter} records across all trial tables in ${timeInSeconds} seconds.`);
      
      // Reload data to refresh the UI
      await loadData();
      
      // Clear success message after 10 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 10000);
      
    } catch (err) {
      console.error("Error purging trial data:", err);
      setError("Failed to purge trial data: " + err.message);
    } finally {
      setIsPurging(false);
    }
  };
  
  // Format date string to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };
  
  return (
    <div className="trial-management-dashboard">
      <div className="dashboard-header">
        <h1>Trial Management Dashboard</h1>
        <div className="dashboard-tabs">
          <button 
            className={viewMode === 'usage' ? 'active' : ''} 
            onClick={() => setViewMode('usage')}
          >
            Usage Data
          </button>
          <button 
            className={viewMode === 'blacklist' ? 'active' : ''} 
            onClick={() => setViewMode('blacklist')}
          >
            Blacklist Management
          </button>
        </div>
      </div>
      
      {/* Error and success messages */}
      {error && <div className="error-message">{error}</div>}
      {actionSuccess && <div className="success-message">{actionSuccess}</div>}
      
      {/* View selector */}
      <div className="dashboard-content">
        {isLoading || isDeletingUser || isPurging ? (
          <div style={{ height: "200px", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Spinner 
              size="medium" 
              color="#0078ff"
              label={
                isPurging ? "Purging all trial data..." : 
                isDeletingUser ? "Deleting user data..." : 
                "Loading data..."
              } 
            />
          </div>
        ) : viewMode === 'usage' ? (
          <>
            {/* Usage summary cards */}
            <div className="usage-summary">
              <div className="summary-card">
                <h3>Total Prompts Used</h3>
                <p className="summary-number">{usageSummary.totalPrompts}</p>
              </div>
              <div className="summary-card">
                <h3>Active Trial Users</h3>
                <p className="summary-number">{usageSummary.activeUsers}</p>
              </div>
              <div className="summary-card">
                <h3>Completed Trials</h3>
                <p className="summary-number">{usageSummary.completedTrials}</p>
              </div>
            </div>
            
            {/* NEW: Purge All Data button */}
            <div className="admin-actions">
              <button 
                className="purge-button"
                onClick={purgeAllTrialData}
                disabled={isPurging}
              >
                <span style={{ marginRight: '6px' }}>⚠️</span>
                {isPurging ? 'Purging All Data...' : 'Purge ALL Trial Data'}
              </button>
              <div className="admin-action-warning">
                This will permanently delete ALL trial data and allow all users to start fresh trials.
              </div>
            </div>
            
            {/* Usage data table */}
            <div className="data-table-container">
              <h2>Trial Usage by User</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fingerprint ID</th>
                    <th>Started At</th>
                    <th>Last Used</th>
                    <th>Prompts Used</th>
                    <th>Remaining</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trialUsage.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-data">No trial usage data found</td>
                    </tr>
                  ) : (
                    trialUsage.map((user, index) => (
                      <tr key={index}>
                        <td className="fingerprint-cell" title={user.fingerprint}>
                          {user.fingerprint.substring(0, 8)}...
                        </td>
                        <td>{formatDate(user.startedAt)}</td>
                        <td>{user.lastUsed ? formatDate(user.lastUsed) : 'N/A'}</td>
                        <td>{user.promptCount || 0}</td>
                        <td>{user.remaining !== undefined ? user.remaining : 'N/A'}</td>
                        <td>
                          <span className={`status-badge ${user.isActive ? 'active' : 'completed'}`}>
                            {user.isActive ? 'Active' : 'Completed'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button 
                              className="blacklist-button"
                              onClick={() => {
                                setFingerprint(user.fingerprint);
                                setViewMode('blacklist');
                              }}
                            >
                              Blacklist
                            </button>
                            <button 
                              style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                border: 'none',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: '#722ed1',
                                color: 'white',
                                opacity: isDeletingUser ? '0.5' : '1'
                              }}
                              onClick={() => deleteUserData(user.fingerprint, user)}
                              disabled={isDeletingUser}
                              title="Delete all user data from Firebase"
                            >
                              <span style={{ fontSize: '14px', lineHeight: '1' }}>🗑️</span> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            {/* Blacklist management */}
            <div className="blacklist-form">
              <h2>Add User to Blacklist</h2>
              <div className="form-group">
                <input
                  type="text"
                  value={fingerprint}
                  onChange={(e) => setFingerprint(e.target.value)}
                  placeholder="Enter user fingerprint ID"
                />
                <button 
                  onClick={addToBlacklist}
                  disabled={isLoading || !fingerprint.trim()}
                >
                  Add to Blacklist
                </button>
              </div>
            </div>
            
            {/* Blacklist table */}
            <div className="data-table-container">
              <h2>Blacklisted Users</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fingerprint ID</th>
                    <th>Added On</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blacklistedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="no-data">No blacklisted users found</td>
                    </tr>
                  ) : (
                    blacklistedUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="fingerprint-cell" title={user.fingerprint}>
                          {user.fingerprint.substring(0, 8)}...
                        </td>
                        <td>{formatDate(user.timestamp)}</td>
                        <td>{user.reason || 'Not specified'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button 
                              className="remove-button"
                              onClick={() => removeFromBlacklist(user.id)}
                            >
                              Remove
                            </button>
                            <button 
                              style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                border: 'none',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: '#722ed1',
                                color: 'white',
                                opacity: isDeletingUser ? '0.5' : '1'
                              }}
                              onClick={() => deleteUserData(user.fingerprint, { fingerprint: user.fingerprint })}
                              disabled={isDeletingUser}
                              title="Delete all user data from Firebase"
                            >
                              <span style={{ fontSize: '14px', lineHeight: '1' }}>🗑️</span> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      
      <div className="dashboard-footer">
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <span style={{ color: '#666', fontSize: '12px' }}>Version 0.1</span>
          <button className="refresh-button" onClick={loadData} disabled={isLoading || isDeletingUser || isPurging}>
            {isLoading ? 'Loading...' : 'Refresh Data'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrialManagementDashboard;